import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogTrigger,
  SkeletonSpinnerCentered,
  Tooltip,
} from '@civilgrid/ui/components';
import { Link, useParams } from '@tanstack/react-router';
import { area, bbox } from '@turf/turf';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { calculateWatershedRisk, getProject, printProjectPDF } from '~/api/projects2';
import { query } from '~/api/tiles';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyDownloadError } from '~/common/notifyDownloadError';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Project as ProjectBase } from '~/common/types';
import { SendEmailModal } from '~/components2/SendEmailModal';
import type { ProjectDetailsParams } from '..';

interface ProjectWithWatershed extends ProjectBase {
  watershedRiskStudy: {
    rFactor: number;
    kFactor: number;
    lsFactor: number;
    watershedErosionEstimate: number;
    siteSedimentRiskFactor: string;
    receivingWaterRisk: boolean;
    combinedEstimate: string;
  };
}

const requiredFields: string[] = ['Start Date', 'End Date', 'Construction Start Date', 'Construction End Date'];

export async function printSwpppReport(projectId: string, ccEmails?: string[]) {
  try {
    await printProjectPDF(projectId, 'swppp', [], [], ccEmails);
    notifyExportQueued({
      title: 'SWPPP Report',
      message: 'Your PDF download has started. You will receive the PDF report in your email in the next few minutes.',
      ccEmails,
    });

    captureTypedPosthogEvent(POSTHOG_EVENTS.environmentalSwpppExported, {
      project_id: projectId,
    });
  } catch (err) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.environmentalSwpppExportFailed, {
      project_id: projectId,
      reason: getReasonForError(err),
    });
    notifyDownloadError();
    return false;
  }
}

export default function SWPPPTool() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const [isLoading, setLoading] = useState(true);
  const [soilsData, setSoilsData] = useState('');
  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  const [watershedRiskStudy, setWatershedRiskStudy] = useState<{
    rFactor: number;
    kFactor: number;
    lsFactor: number;
    watershedErosionEstimate: number;
    siteSedimentRiskFactor: string;
    receivingWaterRisk: boolean;
    combinedEstimate: string;
  } | null>(null);
  const [projectDetails, setProjectDetails] = useState<{
    area: number;
    name: string;
  }>({ area: 0, name: '' });

  const [exportOpen, setExportOpen] = useState(false);

  const containerDiv = useRef<HTMLDivElement>(null);

  async function calculateWatershedRiskOnServer() {
    try {
      setLoading(true);
      setWatershedRiskStudy(null);
      const getWatershedRisk = await calculateWatershedRisk(projectId);
      setWatershedRiskStudy(getWatershedRisk);
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    (async () => {
      const p = await getProject<ProjectWithWatershed>(projectId);

      if (!p.startDate || !p.endDate || !p.constructionStartDate || !p.constructionEndDate) {
        setIsDisabled(true);
        return;
      } else {
        setIsDisabled(false);
      }

      // calculate project area in acres
      const pArea = area(p.geometry) / 4046.86;
      setProjectDetails({ area: pArea, name: p.name });

      if (isEmpty(p.watershedRiskStudy)) {
        // call server
        calculateWatershedRiskOnServer();
      } else {
        setWatershedRiskStudy(p.watershedRiskStudy);
        setLoading(false);
      }

      // get soils data
      const soils = await query('soils', bbox(p.geometry) as [number, number, number, number], 12);
      const soilsString = Array.from(new Set(soils.map((f: any) => f.properties.Taxonomic_Orders))).join(', ');

      setSoilsData(soilsString);
    })();
  }, [projectId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const dialogContent = useMemo(() => {
    if (isDisabled) {
      return (
        <div className="flex flex-col space-y-4">
          <div>This project is missing required fields to run the SWPPP tool.</div>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center gap-1 text-2xs font-medium uppercase">
              <span className="align-super text-red-500">*</span>
              Required fields:
            </div>
            <div className="">
              {requiredFields.map((f) => (
                <div key={f} className="text-2xs">
                  {f}
                </div>
              ))}
            </div>
          </div>

          <div>Add required fields in the Details tab.</div>
          <Link to="/projects/$projectId/$tabId" params={{ projectId, tabId: 'details' }}>
            <Button data-testid="swppp-details-tab-button">Details Tab</Button>
          </Link>
        </div>
      );
    }

    if (isLoading === true || watershedRiskStudy === null) {
      return <SkeletonSpinnerCentered />;
    }

    return (
      <div ref={containerDiv}>
        <div className="my-2 flex flex-wrap gap-2">
          <Button onClick={calculateWatershedRiskOnServer} data-testid="swppp-run-report-button">
            Run report again
          </Button>
          <Button onClick={() => setExportOpen(true)} data-testid="swppp-export-pdf-button">
            Export to PDF
          </Button>
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:gap-2">
          <div className="w-full md:w-1/2">
            <h4 className="font-bold">Sediment Risk</h4>
            <table className="w-full border text-left">
              <thead className="border">
                <tr>
                  <th>Risk Factor</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody className="w-full table-auto border text-left">
                <tr>
                  <td className="border p-1">R Factor</td>
                  <td className="border p-1">{watershedRiskStudy.rFactor.toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="border p-1">K Factor</td>
                  <td className="border p-1">{watershedRiskStudy.kFactor.toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="border p-1">LS Factor</td>
                  <td className="border p-1">{watershedRiskStudy.lsFactor.toFixed(2)}</td>
                </tr>
                <tr className="font-bold">
                  <td className="border p-1">
                    <Tooltip
                      text="This estimate is for only this site, not the entire watershed, only during the period of construction."
                      container={containerDiv.current}
                      delayDuration={10}
                      data-testid="swppp-watershed-erosion-estimate-info"
                    >
                      Watershed Erosion Estimate
                    </Tooltip>
                  </td>
                  <td className="border p-1">
                    {`${watershedRiskStudy.watershedErosionEstimate.toFixed(2)} tons/acre`}
                  </td>
                </tr>
              </tbody>
            </table>
            {soilsData && <div className="my-2"> Soil Types: {soilsData}</div>}
            <div className="my-2">
              Site Sediment Risk Factor is{' '}
              <span className="font-bold">{watershedRiskStudy.siteSedimentRiskFactor.toUpperCase()}</span>
            </div>
          </div>
          <div className="w-full md:w-1/2 md:pt-8">
            <h5>
              <i>Data Sources</i>
            </h5>
            <p>
              R Factor calculations come from the{' '}
              <a
                href="https://lew.epa.gov/"
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-50 underline hover:text-blue-60"
              >
                EPA LEW tool
              </a>
              . These calculations use <i>Construction Start</i> and <i>Construction End</i> dates from the project,
              which can be edited on the{' '}
              <Link
                to="/projects/$projectId/$tabId"
                params={{ projectId: projectId!, tabId: 'details' }}
                className="text-blue-50 underline hover:text-blue-60"
              >
                Project Info
              </Link>{' '}
              tab.
            </p>
            <p>
              K Factor and LS Factors come from the{' '}
              <a
                href="http://svctenvims.dot.ca.gov/wqpt/wqpt.aspx"
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-50 underline hover:text-blue-60"
              >
                Caltrans Water Quality Planning Tool
              </a>
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:gap-2">
          <div className="w-full md:w-1/2">
            <h4 className="font-bold">Receiving Water Risk</h4>
            <div className="my-2 ">
              Receiving Water Risk is{' '}
              <span className="font-bold">{watershedRiskStudy.receivingWaterRisk ? 'HIGH' : 'LOW'}</span>
            </div>
          </div>
          <div className="w-full md:w-1/2">
            <h5>
              <i>Data Sources</i>
            </h5>
            <p>
              High Receiving Water Risk Watersheds come from the{' '}
              <a
                href="http://svctenvims.dot.ca.gov/wqpt/wqpt.aspx"
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-50 underline hover:text-blue-60"
              >
                Caltrans Water Quality Planning Tool
              </a>
              . More details on their methodology can be found{' '}
              <a
                href="https://www.waterboards.ca.gov/water_issues/programs/stormwater/docs/constpermits/guidance/receivingwaterrisk.pdf"
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-50 underline hover:text-blue-60"
              >
                here.
              </a>{' '}
              The EPA publishes additional methodologies here{' '}
              <a
                href="https://www.epa.gov/rps/rps-methodology-comparing-watersheds-evaluating-options"
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-50 underline hover:text-blue-60"
              >
                here
              </a>
            </p>
          </div>
        </div>

        <h4 className="font-bold">Combined Risk</h4>
        <div className="my-2 ">
          The Combined Risk is <span className="font-bold">{watershedRiskStudy.combinedEstimate.toUpperCase()}</span>
        </div>
      </div>
    );
  }, [isDisabled, isLoading, watershedRiskStudy, projectId]);

  return (
    <div className="space-y-2">
      <div className="flex space-x-2">
        <Dialog>
          <DialogTrigger asChild>
            <Button data-testid="swppp-tool-open-button">SWPPP Tool</Button>
          </DialogTrigger>
          <DialogContent
            title={`SWPPP Report for ${projectDetails.name} (${projectDetails.area.toFixed(0)} acres)`}
            className="z-[110] w-auto min-w-min max-w-[75vw]"
            overlayClassName="z-[100]"
            data-testid="swppp-tool-dialog"
          >
            <DialogBody>{dialogContent}</DialogBody>
          </DialogContent>
        </Dialog>
      </div>
      <SendEmailModal
        open={exportOpen}
        onOpenChange={setExportOpen}
        title="Export SWPPP Report"
        testIdPrefix="swppp-export"
        onSend={(ccEmails) => printSwpppReport(projectId, ccEmails.length > 0 ? ccEmails : undefined)}
      />
    </div>
  );
}
